import styled from 'styled-components'
import { useRef, useState } from 'react'
import { useAtom } from 'jotai'
import { Button, Icon, TextField, Typography, Popover } from '@equinor/eds-core-react'
import { camera, close, error_outlined, help_outline, image_add } from '@equinor/eds-icons'

import { imagesAtom, descriptionsAtom, showImageErrorsAtom } from '@/core/store/globalStore'
import { ExistingImageDto } from '@/core/models/types'
import {
    InputStyledLabel,
    StyledErrorIcon,
    StyledErrorTypography,
    StyledHelpButton,
    StyledHelpIcon,
} from '@/styles/layout'
import TakePhotoOnboarding from './TakePhotoOnboarding'
import { renameFile, renameFilesSequentially, updateAtIndex } from '../utils'
import PopoverDialog from '@/components/popover/PopoverDialog'

interface UploadImageProps {
    existingImages: ExistingImageDto[]
    onUpdateExistingImages: (images: ExistingImageDto[]) => void
}

const UploadImage: React.FC<UploadImageProps> = ({ existingImages, onUpdateExistingImages }) => {
    const [images, setImages] = useAtom(imagesAtom)
    const [descriptions, setDescriptions] = useAtom(descriptionsAtom)
    const [showImageErrors, setShowImageErrors] = useAtom(showImageErrorsAtom)
    const [showCamera, setShowCamera] = useState(false)

    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const openFilePicker = () => {
        fileInputRef.current?.click()
    }

    const appendNewImage = (file: File) => {
        setImages((prev) => [...prev, renameFile(file, prev.length + 1)])
        setDescriptions((prev) => [...prev, ''])
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0]
        if (!selectedFile) return

        appendNewImage(selectedFile)
        e.target.value = ''
    }

    const handleCameraImage = (file: File) => {
        if (!file) return
        appendNewImage(file)
    }

    const handleNewDescriptionChange = (index: number, value: string) => {
        setDescriptions((prev) => updateAtIndex(prev, index, value))
        setShowImageErrors(false)
    }

    const handleExistingDescriptionChange = (index: number, value: string) => {
        onUpdateExistingImages(
            existingImages.map((image, i) =>
                i === index
                    ? {
                          ...image,
                          description: value,
                      }
                    : image
            )
        )
        setShowImageErrors(false)
    }

    const removeNewImage = (index: number) => {
        setImages((prev) => renameFilesSequentially(prev.filter((_, i) => i !== index)))
        setDescriptions((prev) => prev.filter((_, i) => i !== index))
    }

    const removeExistingImage = (index: number) => {
        onUpdateExistingImages(existingImages.filter((_, i) => i !== index))
    }

    return (
        <FormContainer>
            <RowStyle>
                <HiddenFileInput ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} />

                <StyledUploadButton onClick={openFilePicker} variant="outlined">
                    <StyledIcon data={image_add} size={24} />
                    Upload image
                </StyledUploadButton>

                <ScannerStyledButton onClick={() => setShowCamera(true)}>
                    <StyledIcon data={camera} size={24} />
                    Capture Photo
                </ScannerStyledButton>
            </RowStyle>

            <ImageList>
                {existingImages.map((image, index) => (
                    <ImageItem
                        key={`existing-${image.url}-${index}`}
                        previewSrc={image.url ?? ''}
                        description={image.description ?? ''}
                        hasError={showImageErrors && !image.description}
                        onRemove={() => removeExistingImage(index)}
                        onDescriptionChange={(value) => handleExistingDescriptionChange(index, value)}
                    />
                ))}

                {images.map((file, index) => (
                    <ImageItem
                        key={file.name}
                        previewSrc={URL.createObjectURL(file)}
                        description={descriptions[index] ?? ''}
                        hasError={showImageErrors && !descriptions[index]}
                        onRemove={() => removeNewImage(index)}
                        onDescriptionChange={(value) => handleNewDescriptionChange(index, value)}
                    />
                ))}
            </ImageList>

            {showCamera && (
                <TakePhotoOnboarding
                    showImage={showCamera}
                    onClose={() => setShowCamera(false)}
                    addImage={handleCameraImage}
                />
            )}
        </FormContainer>
    )
}

interface ImageItemProps {
    previewSrc: string
    description: string
    hasError: boolean
    onRemove: () => void
    onDescriptionChange: (value: string) => void
}

const ImageItem: React.FC<ImageItemProps> = ({ previewSrc, description, hasError, onRemove, onDescriptionChange }) => {
    const [isTooltipDescriptionOpen, setTooltipDescriptionIsOpen] = useState(false)
    const descriptionRef = useRef<HTMLInputElement>(null)
    const tooltipImage = 'Describe the purpose of this image. Max 240 characters.'

    return (
        <ImageRow>
            <PreviewWrapper>
                <StyledRemoveButton onClick={onRemove} type="button">
                    <Icon data={close} color="#FFFFFF" />
                </StyledRemoveButton>

                <PreviewImage src={previewSrc} alt="Preview" />
            </PreviewWrapper>

            <DescriptionWrapper>
                <StyledTextField
                    id="ImageDescription"
                    label={
                        <RowStyle style={{ alignItems: 'center' }}>
                            <InputStyledLabel>Image description</InputStyledLabel>

                            <StyledHelpButton
                                ref={descriptionRef}
                                variant="ghost_icon"
                                aria-label="description help"
                                onClick={() => setTooltipDescriptionIsOpen(!isTooltipDescriptionOpen)}
                                $isActive={isTooltipDescriptionOpen}
                            >
                                <StyledHelpIcon data={help_outline} size={24} $isActive={isTooltipDescriptionOpen} />
                            </StyledHelpButton>
                        </RowStyle>
                    }
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        onDescriptionChange(e.target.value)
                    }}
                    onBlur={() => {
                        onDescriptionChange(description.trim())
                    }}
                    required
                    maxLength={240}
                    variant={hasError ? 'error' : undefined}
                    meta={
                        <RowStyle>
                            <InputStyledLabel>Mandatory</InputStyledLabel>
                        </RowStyle>
                    }
                    $isError={hasError && !description}
                />

                {hasError && (
                    <ErrorRow>
                        <StyledErrorIcon data={error_outlined} size={16} />
                        <StyledErrorTypography>Please enter an image description.</StyledErrorTypography>
                    </ErrorRow>
                )}
            </DescriptionWrapper>
            <PopoverDialog
                open={isTooltipDescriptionOpen}
                anchorEl={descriptionRef.current}
                onClose={() => setTooltipDescriptionIsOpen(false)}
                title={'Image description'}
                content={tooltipImage}
            />
        </ImageRow>
    )
}

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-left: 16px;
    margin-right: 16px;
`

const HiddenFileInput = styled.input`
    display: none;
`

const RowStyle = styled.div`
    display: flex;
    flex: 1;
    align-items: flex-start;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
`

const ErrorRow = styled(RowStyle)`
    margin-top: 4px;
    margin-bottom: 0;
`

const ImageList = styled.div``

const ImageRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    width: 100%;
    margin-bottom: 8px;
`

const PreviewWrapper = styled.div`
    position: relative;
    width: 88px;
    height: 68px;
`

const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`

const DescriptionWrapper = styled.div`
    flex: 1;
`

const StyledTextField = styled(TextField)<{ $isError?: boolean }>`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    color: white;
    margin-bottom: 0;

    @media (max-width: 1024px) {
        --eds_interactive_danger__resting: #c6002d;
        --eds_interactive_danger__hover: #c6002d;
        input {
            background-color: #f5f5f5;
            color: #1d1d1d;
        }
        .leTuEP {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }

        .kwyFQC {
            color: ${(props) => (props.$isError ? '#a50827' : '#8a4100')};
        }
    }
`

const StyledPopoverContent = styled(Typography)`
    max-width: 280px;
    white-space: normal;
    overflow-wrap: anywhere;
`

const StyledUploadButton = styled(Button)`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background: white;
    color: #007079;
    border-radius: 4px;
    border: 1px solid;
    border-color: #007079;
    gap: 8px;
    padding: 6px;

    @media (max-width: 1024px) {
        background: #ffffff;
        color: #1f6369;
        border-color: #21767e;

        &:hover {
            border-color: #21767e;
            background-color: #f5f5f5;
            color: #1f6369;
        }
    }
`

const ScannerStyledButton = styled(Button)`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    background: #007079;
    color: white;
    border-radius: 4px;
    border: 1px solid;
    border-color: #007079;
    gap: 8px;
    padding: 6px;
    font-size: 14px;
    white-space: nowrap;

    &:hover {
        border-color: #004f55;
        background-color: #004f55;
    }
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    width: 24px;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const StyledRemoveButton = styled.button`
    position: absolute;
    top: 5px;
    right: 5px;
    height: 20px;
    width: 20px;
    padding: 0;
    cursor: pointer;
    background-color: #6f6f6f;
    border: none;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: #1c1c1c;
    }
`

export default UploadImage
